import { useState } from 'react';
import { formatDateForSelect } from '@/lib/utils';
import {
  Button,
  Card,
  CardContent
} from "@geely/ui";
import { Calendar, CheckCircle2, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

interface DateCarouselProps {
  title: string;
  dates: string[];
  currentDate: string;
  onDateClick: (date: string) => void;
  compact?: boolean;
}

export function DateCarousel({ title, dates, currentDate, onDateClick, compact = false }: DateCarouselProps) {
  const pageSize = compact ? 4 : 7;
  const [currentStart, setCurrentStart] = useState(0);
  const displayedDates = dates.slice(currentStart, currentStart + pageSize);

  const goPrevious = () => setCurrentStart(Math.max(0, currentStart - pageSize));
  const goNext = () => setCurrentStart(Math.min(dates.length - pageSize, currentStart + pageSize));

  if (dates.length === 0) return null;

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-medium text-[#080404]">{title}</h3>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={goPrevious}
            disabled={currentStart === 0}
            className="h-8 w-8 p-0"
          >
            <ChevronLeft className="size-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={goNext}
            disabled={currentStart >= dates.length - pageSize}
            className="h-8 w-8 p-0"
          >
            <ChevronRight className="size-4" />
          </Button>
        </div>
      </div>
      <div className={cn(
        "grid gap-2",
        compact
          ? "grid-cols-2 md:grid-cols-4"
          : "grid-cols-3 md:grid-cols-4 lg:grid-cols-7"
      )}>
        {displayedDates.map(date => {
          const isSelected = currentDate === date;
          const dateLabel = formatDateForSelect(date);
          const [dayOfWeek, dayMonthYear] = dateLabel.split(', ');

          return (
            <Card
              key={date}
              className={cn(
                "cursor-pointer transition-all hover:shadow-md",
                isSelected
                  ? "border-[#000000] border-2 bg-[#000000]/5"
                  : "border-[#e2e8f0] hover:border-[#000000]/30"
              )}
              onClick={() => onDateClick(date)}
            >
              <CardContent className="p-3 text-center">
                <div className="flex flex-col items-center gap-1">
                  <Calendar className={cn(
                    "size-5",
                    isSelected ? "text-[#000000]" : "text-[#64748b]"
                  )} />
                  <div>
                    <p className={cn(
                      "font-semibold text-xs",
                      isSelected ? "text-[#000000]" : "text-[#080404]"
                    )}>
                      {dayOfWeek}
                    </p>
                    <p className="text-[10px] text-[#64748b]">{dayMonthYear}</p>
                  </div>
                  {isSelected && (
                    <CheckCircle2 className="size-3 text-[#000000]" />
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
